import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { RootState } from '@/plugins/store';
import { ActionTree } from 'vuex';
import PreventifService, { FilterPreventif, PERIOD } from '../services/PreventifService';
import { PreventifActions, PreventifActionType } from './action-types';
import { PREVENTIF } from './mutation-types';
import { PreventifState } from './state';
import dayjs from 'dayjs';
import { Site } from '@/models/Site';

export const actions: ActionTree<PreventifState, RootState> = {
    async [PreventifActions.GET_PREVENTIF](
        { state, commit }: PreventifActionType,
        payload: { dateDebut: string; dateFin: string, site?: Site, lastSiteId?: number, isForInfiniteScroll?: boolean, isMonoSite: boolean }
    ): Promise<void> {
        await getPreventif({state, commit},
            {dateDebut: payload.dateDebut,
                    dateFin: payload.dateFin,
                    siteId: payload.site?.id,
                    lastSiteId: payload.lastSiteId,
                    isForInfiniteScroll: payload.isForInfiniteScroll,
                    isMonoSite: payload.isMonoSite},
                    PERIOD.NOW);
    },
    async [PreventifActions.GET_XLS](
        { state, commit }: PreventifActionType,
        payload: { dateDebut: string; dateFin: string, site?: Site, isMonoSite: boolean }
    ): Promise<void> {
        await getXls({state, commit}, {dateDebut: payload.dateDebut, dateFin: payload.dateFin, siteId: payload.site?.id,isMonoSite: payload.isMonoSite });
    },

    async [PreventifActions.NEXT](
        { state, commit }: PreventifActionType,
        payload: { dateDebut: string; dateFin: string, site?: Site, isMonoSite: boolean }
    ): Promise<boolean> {
        return getPreventif({ state, commit }, { dateDebut: payload.dateDebut, dateFin: payload.dateFin, siteId: payload.site?.id, isMonoSite: payload.isMonoSite }, PERIOD.AFTER);
    },

    async [PreventifActions.PREV](
        { state, commit }: PreventifActionType,
        payload: { dateDebut: string; dateFin: string, site?: Site, isMonoSite: boolean }
    ): Promise<boolean> {
        return getPreventif({ state, commit }, { dateDebut: payload.dateDebut, dateFin: payload.dateFin, siteId: payload.site?.id, isMonoSite: payload.isMonoSite }, PERIOD.BEFORE);
    },

};

async function getPreventif(
    { state, commit }: PreventifActionType,
    payload: { dateDebut: string; dateFin: string, siteId?: number, lastSiteId?: number, isForInfiniteScroll?: boolean, isMonoSite: boolean },
    periode: PERIOD,
){
    commit(PREVENTIF.SET_LOADING, true);
    const params: FilterPreventif = {
        dateDebut: payload.dateDebut,
        dateFin: payload.dateFin,
        periodicitePlanning: state.periodicite,
        visionPlanning: state.previsionnel,
        siteId: payload.siteId,
        lastSiteId: payload.lastSiteId,
        isMonoSite: payload.isMonoSite,
    };

    try {
        const response = await PreventifService.getListPreventif(params);

        commit(PREVENTIF.IS_ALL_DATA, response.data.isAllData);
        if (response.data.planningPreventifNoeudDTO !== undefined && response.data.planningPreventifNoeudDTO.length === 20) {
            commit(PREVENTIF.SET_LAST_SITE_ID, response.data.planningPreventifNoeudDTO[response.data.planningPreventifNoeudDTO.length - 1].id);
        }

        const data2Header = PreventifService.genererPeriodes(dayjs(params.dateDebut), dayjs(params.dateFin), params.periodicitePlanning);

        if(periode === PERIOD.BEFORE) {
            commit(PREVENTIF.SET_HEADER, [...data2Header, ...state.header]);
            commit(PREVENTIF.ADD_BEFORE, response.data.planningPreventifNoeudDTO);
            commit(PREVENTIF.SET_PAGE, 0);
        } else if(periode === PERIOD.NOW) {
            commit(PREVENTIF.SET_HEADER, data2Header);
            if (payload.lastSiteId === undefined) {
                if (response.data.planningPreventifNoeudDTO !== undefined) {
                    commit(PREVENTIF.SET_ITEMS, response.data.planningPreventifNoeudDTO);
                } else {
                    commit(PREVENTIF.SET_ITEMS, []);
                }
            } else if (response.data.planningPreventifNoeudDTO !== undefined){
                commit(PREVENTIF.ADD_ITEMS, response.data.planningPreventifNoeudDTO);
            }
            commit(PREVENTIF.SET_PAGE, 0);
        } else if(periode === PERIOD.AFTER) {
            commit(PREVENTIF.SET_HEADER, [...state.header, ...data2Header]);
            commit(PREVENTIF.ADD_AFTER, response.data.planningPreventifNoeudDTO);
            commit(PREVENTIF.SET_PAGE, state.page + state.nbPeriodesStep);
        }

        commit(PREVENTIF.SET_DISPLAY);
        commit(PREVENTIF.SET_LOADING, false);
        return data2Header.length > 0;
    } catch (error) {
        commit(PREVENTIF.SET_ERROR, error);
        commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        commit(PREVENTIF.SET_LOADING, false);
        return false;
    }

}

async function getXls(
    { state, commit }: PreventifActionType,
    payload: { dateDebut: string; dateFin: string, siteId?: number, isMonoSite: boolean }
) {
    commit(PREVENTIF.SET_LOADING_XLS_PREVENTIF, true);
    const params: FilterPreventif = {
        dateDebut: payload.dateDebut,
        dateFin: payload.dateFin,
        periodicitePlanning: state.periodicite,
        visionPlanning: state.previsionnel,
        siteId: payload.siteId,
        isMonoSite: payload.isMonoSite
    };

    try {
        const response = await PreventifService.getExportXlsInterventions(params);
        if (response.status === 200 && response.data !== undefined) {
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'Export_interventions_preventives.xlsx';
            link.click();
            URL.revokeObjectURL(link.href);
        } else {
            commit(
                `errorToaster/${ERROR.SET_MESSAGE}`,
                await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                { root: true }
            );
        }
        commit(PREVENTIF.SET_LOADING_XLS_PREVENTIF, false);
    } catch (error) {
        commit(PREVENTIF.SET_ERROR, error);
        commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        commit(PREVENTIF.SET_LOADING, false);
        return false;
    }
}
